'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'

import {
  Article,
  HeroSimple,
  Heading,
  Paragraph,
  Section,
  Form,
  FormGroup,
  FormInput,
  FormLabel,
  FormInvalidMessage,
  Button,
  ButtonTitle,
  ButtonIcon,
  cn,
} from '@systhemaui/next'

import {
  AuthCard,
  SocialLoginButtons,
  SocialLoginSkeleton,
  PasswordInput,
  Separator,
  type EnabledProviders,
} from '@/components/auth'

import { signIn } from '@/lib/auth-client'
import { validateRedirectUrl } from '@/utils/redirectUrl'
import { ACCOUNT_DELETION_CONTINUE_PATH } from '@/lib/account-deletion-response'

const GENERIC_WRONG_PASSWORD =
  'Hibás jelszó. Ha elfelejtetted, kattints az „Elfelejtett jelszó?" linkre.'
const NO_ACCOUNT_MESSAGE =
  'Nem találunk fiókot ezzel az e-mail címmel. Ellenőrizd, hogy jól írtad-e be, vagy regisztrálj.'
const FALLBACK_LOGIN_ERROR = 'Hibás e-mail cím vagy jelszó'

const PROVIDER_LABELS: Record<string, string> = {
  google: 'Google',
  facebook: 'Facebook',
  apple: 'Apple',
}

const SOCIAL_PROVIDERS = new Set(['google', 'facebook', 'apple'])

type LoginErrorField = 'email' | 'password'
type LoginErrorResult = { field: LoginErrorField; message: string }

async function resolveLoginErrorMessage(email: string): Promise<LoginErrorResult> {
  try {
    const res = await fetch('/api/auth/check-provider', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    if (!res.ok) return { field: 'password', message: FALLBACK_LOGIN_ERROR }
    const data = (await res.json()) as { providers?: string[] }
    const providers = data.providers ?? []

    if (providers.length === 0) {
      return { field: 'email', message: NO_ACCOUNT_MESSAGE }
    }

    if (providers.includes('credential')) {
      return { field: 'password', message: GENERIC_WRONG_PASSWORD }
    }

    const socials = providers.filter((p) => SOCIAL_PROVIDERS.has(p))
    if (socials.length === 1) {
      const p = socials[0]
      if (p === 'google') {
        return {
          field: 'email',
          message:
            'Ez az e-mail cím Google fiókkal van regisztrálva. Lépj be fent a Google gombbal.',
        }
      }
      if (p === 'facebook') {
        return {
          field: 'email',
          message:
            'Ez az e-mail cím Facebook fiókkal van regisztrálva. Lépj be fent a Facebook gombbal.',
        }
      }
      if (p === 'apple') {
        return {
          field: 'email',
          message:
            'Ez az e-mail cím Apple fiókkal van regisztrálva. Lépj be fent az Apple gombbal.',
        }
      }
    }
    if (socials.length > 1) {
      const labels = socials.map((p) => PROVIDER_LABELS[p] ?? p).join(', ')
      return {
        field: 'email',
        message: `Ez az e-mail cím közösségi fiókkal van regisztrálva (${labels}). Lépj be fent az egyik közösségi gombbal.`,
      }
    }

    return { field: 'password', message: FALLBACK_LOGIN_ERROR }
  } catch {
    return { field: 'password', message: FALLBACK_LOGIN_ERROR }
  }
}

type LoginPageClientProps = {
  redirectParam?: string
  oauthError?: string
}

export default function LoginPageClient({ redirectParam, oauthError }: LoginPageClientProps) {
  const validatedRedirect = validateRedirectUrl(redirectParam ?? null)
  const isConfirmingDeletion = validatedRedirect?.split('?')[0] === ACCOUNT_DELETION_CONTINUE_PATH
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [enabledProviders, setEnabledProviders] = useState<EnabledProviders | null>(null)
  const [socialLoading, setSocialLoading] = useState<'google' | 'facebook' | 'apple' | null>(null)

  const socialError = oauthError
    ? oauthError === 'email_not_found'
      ? 'A belépéshez szükségünk van az e-mail címedre. A közösségi fiókod beállításaiban engedélyezd az e-mail megosztását, majd próbáld újra. Vagy regisztrálj e-mail címmel és jelszóval lent.'
      : oauthError === 'state_mismatch'
        ? 'A belépési folyamat megszakadt (lehet, hogy lejárt vagy duplán kattintottál). Kattints újra a közösségi belépés gombra.'
        : 'Nem sikerült a közösségi belépés. Próbáld újra, vagy lépj be e-mail címmel és jelszóval lent.'
    : null

  useEffect(() => {
    fetch('/api/auth/providers')
      .then((res) => res.json())
      .then((data: EnabledProviders) => setEnabledProviders(data))
      .catch(() => setEnabledProviders({ google: false, facebook: false, apple: false }))
  }, [])

  useEffect(() => {
    const reset = () => setSocialLoading(null)
    window.addEventListener('pageshow', reset)
    window.addEventListener('focus', reset)
    return () => {
      window.removeEventListener('pageshow', reset)
      window.removeEventListener('focus', reset)
    }
  }, [])

  const validateForm = (): boolean => {
    const newErrors: { email?: string; password?: string } = {}

    if (!email) {
      newErrors.email = 'Kötelező mező'
    } else if (!/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(email)) {
      newErrors.email = 'Érvénytelen e-mail cím'
    }

    if (!password) {
      newErrors.password = 'Kötelező mező'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsSubmitting(true)
    setErrors({})

    try {
      const result = await signIn.email({
        email,
        password,
      })
      if (result.error) {
        const { field, message } = await resolveLoginErrorMessage(email)
        setErrors({ [field]: message })
        setIsSubmitting(false)
      } else {
        window.location.href = validatedRedirect || '/'
      }
    } catch {
      setErrors({ password: 'Hiba történt a belépésnél' })
      setIsSubmitting(false)
    }
  }

  const handleSocialLogin = async (provider: 'google' | 'facebook' | 'apple') => {
    if (socialLoading || isSubmitting) return
    setSocialLoading(provider)
    setErrors({})

    const newUserBaseUrl = validatedRedirect || '/'
    const newUserUrl = newUserBaseUrl.includes('?')
      ? `${newUserBaseUrl}&registered=true`
      : `${newUserBaseUrl}?registered=true`

    try {
      const result = await signIn.social({
        provider,
        callbackURL: validatedRedirect || '/',
        newUserCallbackURL: newUserUrl,
        errorCallbackURL: '/belepes',
      })
      if (result?.error) {
        const status = result.error.status
        const message =
          status === 429
            ? 'Túl sok próbálkozás rövid idő alatt. Várj egy fél percet, és próbáld újra.'
            : 'Nem sikerült elindítani a közösségi belépést. Próbáld újra, vagy lépj be e-mail címmel és jelszóval lent.'
        setErrors({ email: message })
        setSocialLoading(null)
      }
    } catch {
      setErrors({
        email:
          'Nem sikerült elindítani a közösségi belépést. Próbáld újra, vagy lépj be e-mail címmel és jelszóval lent.',
      })
      setSocialLoading(null)
    }
  }

  const hasSocialProviders =
    enabledProviders &&
    (enabledProviders.google || enabledProviders.facebook || enabledProviders.apple)

  return (
    <main id="login">
      <Article theme="default">
        <HeroSimple alignContent="center">
          <Heading.h1>Belépés</Heading.h1>
          <Paragraph type="lead" className="text-balance">
            Lépj be CreatorTV profiloddal, hogy elérhesd a tartalmakat.
          </Paragraph>
        </HeroSimple>

        <Section
          theme="default"
          layoutBackground="base"
          padding={3}
          className="pt-0 [&_.col-mx-3]:lg:col-span-12 [&_.col-mx-3]:lg:col-start-7"
        >
          <AuthCard>
            {isConfirmingDeletion && (
              <Paragraph className="mt-0 mb-4">
                A profilod törlésének megerősítéséhez lépj be azzal a fiókkal, amelynek a törlését
                kérted.
              </Paragraph>
            )}
            {socialError && (
              <div className="mb-4 rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                {socialError}
              </div>
            )}

            {!enabledProviders ? (
              <>
                <SocialLoginSkeleton />
                <Separator />
              </>
            ) : hasSocialProviders ? (
              <>
                <SocialLoginButtons
                  mode="login"
                  enabledProviders={enabledProviders}
                  loadingProvider={socialLoading}
                  disabled={isSubmitting}
                  onFacebookClick={() => handleSocialLogin('facebook')}
                  onGoogleClick={() => handleSocialLogin('google')}
                  onAppleClick={() => handleSocialLogin('apple')}
                />
                <Separator />
              </>
            ) : null}

            <Form disableAnimation onSubmit={handleSubmit}>
              <FormGroup>
                <FormLabel htmlFor="email">E-mail cím</FormLabel>
                <FormInput
                  type="email"
                  id="email"
                  name="email"
                  pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$"
                  placeholder="pl.: gipsz.jakab@creatortv.hu"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                  className={cn(errors.email && 'is-invalid')}
                  required
                  disabled={socialLoading !== null}
                />
                {errors.email && <FormInvalidMessage>{errors.email}</FormInvalidMessage>}
              </FormGroup>

              <FormGroup>
                <div className="flex w-full items-center justify-between">
                  <FormLabel htmlFor="password">Jelszó</FormLabel>
                  <Link
                    href="/elfelejtett-jelszo"
                    className="text-body text-white underline transition-colors hover:text-white/80"
                  >
                    Elfelejtett jelszó?
                  </Link>
                </div>
                <PasswordInput
                  id="password"
                  name="password"
                  value={password}
                  onChange={setPassword}
                  isInvalid={!!errors.password}
                  required
                  disabled={socialLoading !== null}
                />
                {errors.password && <FormInvalidMessage>{errors.password}</FormInvalidMessage>}
              </FormGroup>

              <Button type="submit" disabled={isSubmitting || socialLoading !== null}>
                <ButtonTitle>{isSubmitting ? 'Betöltés...' : 'Belépek'}</ButtonTitle>
                <ButtonIcon
                  dangerouslySetInnerHTML={{
                    __html:
                      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg>',
                  }}
                />
              </Button>
            </Form>

            <Separator />

            <Paragraph className="mt-0 mb-4 w-full text-center">
              Még nincs CreatorTV fiókod?{' '}
              <Link
                href={
                  validatedRedirect
                    ? `/regisztracio?redirect=${encodeURIComponent(validatedRedirect)}`
                    : '/regisztracio'
                }
                className="text-[#FE4C04] hover:underline"
              >
                Regisztrálj itt
              </Link>
            </Paragraph>
          </AuthCard>
        </Section>
      </Article>
    </main>
  )
}
